import { Component, Input, SimpleChanges, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FilterBase } from 'src/app/shared/grid/filter-base';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { InputsModule, SliderModule } from '@progress/kendo-angular-inputs';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'grid-number-range-filter',
    template: ` <div class="container">
        <div class="row">
            <div class="col-3">
                {{ minTitle }}
            </div>
            <div
                class="col-6"
                *ngIf="showSliders">
                <kendo-slider
                    tickPlacement="none"
                    (valueChange)="onFromChange($event)"
                    [min]="min"
                    [max]="max"
                    [smallStep]="smallStep"
                    [(ngModel)]="valueFrom">
                </kendo-slider>
            </div>
            <div class="col-3">
                <kendo-numerictextbox
                    data-qa-id="NumberRangeFilterMinText"
                    [(value)]="valueFrom"
                    [min]="min"
                    [max]="max"
                    [decimals]="0"
                    [format]="format"
                    [autoCorrect]="autoCorrectValue"
                    (valueChange)="onFromChange($event)">
                </kendo-numerictextbox>
            </div>
            <div class="col-3">
                {{ maxTitle }}
            </div>
            <div
                class="col-6"
                *ngIf="showSliders">
                <kendo-slider
                    tickPlacement="none"
                    (valueChange)="onToChange($event)"
                    [min]="min"
                    [max]="max"
                    [smallStep]="smallStep"
                    [(ngModel)]="valueTo">
                </kendo-slider>
            </div>
            <div class="col-3">
                <kendo-numerictextbox
                    data-qa-id="NumberRangeFilterMaxText"
                    [(value)]="valueTo"
                    [min]="min"
                    [max]="max"
                    [decimals]="0"
                    [format]="format"
                    [autoCorrect]="autoCorrectValue"
                    (valueChange)="onToChange($event)">
                </kendo-numerictextbox>
            </div>
        </div>
    </div>`,
    providers: [{ provide: FilterBase, useExisting: GridNumberRangeFilterComponent }],
    standalone: true,
    imports: [SliderModule, InputsModule, FormsModule, CommonModule]
})
export class GridNumberRangeFilterComponent extends FilterBase implements OnInit, OnChanges {
    autoCorrectValue = true;
    valueFrom = 0;
    valueTo = 0;

    @Input() width = 350;
    @Input() minTitle = 'Min.';
    @Input() min = 0;
    @Input() maxTitle = 'Max.';
    @Input() max = 255;
    @Input() smallStep = 1;
    @Input() capMaxFilter? = true;
    @Input() format? = 'n2';
    @Input() showSliders = true;
    @Output() filterValueChanged = new EventEmitter<CompositeFilterDescriptor>();

    ngOnInit(): void {
        // For this case, we need to disable autoCorrect so it does not cap the Max value
        if (this.capMaxFilter === false) {
            this.autoCorrectValue = false;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['filter'] || changes['min']) {
            this.valueFrom = this.getLatestFilterValue(null, this.field + 'From') || this.min;
        }
        if (changes['filter'] || changes['max']) {
            this.valueTo = this.getLatestFilterValue(null, this.field + 'To') || this.max;
        }
    }

    public onFromChange(value: unknown): void {
        this.onRangeChange(value, this.valueTo);
    }

    public onToChange(value: unknown): void {
        this.onRangeChange(this.valueFrom, value);
    }

    private onRangeChange(valueFrom, valueTo): void {
        if (this.capMaxFilter) {
            this.processRangeWithMaxCap(valueFrom, valueTo);
        } else {
            this.processRangeNoMaxCap(valueFrom, valueTo);
        }
        this.filterValueChanged.emit(this.filter);
    }

    /**
     * The TO and FROM fitler values are checked for validity before updating the filter object.
     * 1) Update the FROM filter object if:
     *   - FROM value > MIN (ie: 0)
     *   - FROM value < TO value
     * 2) Update the TO filter object if:
     *   - TO value < MAX (ie: 255)
     *   - FROM value < TO value
     * @param valueFrom
     * @param valueTo
     */
    processRangeWithMaxCap(valueFrom, valueTo): void {
        this.updateFilter({
            field: this.field + 'From',
            operator: 'gte',
            value: valueFrom
        });
        if (valueFrom === this.min) {
            this.removeFilter(this.field + 'From');
        }

        this.updateFilter({
            field: this.field + 'To',
            operator: 'lte',
            value: valueTo
        });
        if (valueTo === this.max) {
            this.removeFilter(this.field + 'To');
        }
    }

    /**
     * The flag:  capMaxFilter=FALSE means that we do not want any checks on the TO, FROM values
     * @param valueFrom
     * @param valueTo
     */
    processRangeNoMaxCap(valueFrom, valueTo): void {
        this.updateFilter({
            field: this.field + 'From',
            operator: 'gte',
            value: valueFrom
        });
        this.valueFrom = valueFrom;
        this.updateFilter({
            field: this.field + 'To',
            operator: 'lte',
            value: valueTo
        });
        this.valueTo = valueTo;
    }
}
